import { exportNoteAsPdf } from '../components/Journal/exportNote';
import { buildExportPdfFilename } from './exportPdfFilename';

function escapeHtml(text) {
    return String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function p(text) {
    const value = String(text || '').trim();
    return value ? `<p>${escapeHtml(value)}</p>` : '';
}

function h2(text) {
    const value = String(text || '').trim();
    return value ? `<h2>${escapeHtml(value)}</h2>` : '';
}

function h3(text) {
    const value = String(text || '').trim();
    return value ? `<h3>${escapeHtml(value)}</h3>` : '';
}

function ul(items) {
    const list = (items || [])
        .map(item => {
            if (item == null) return '';
            if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
            return String(item.title || item.body || item.label || '').trim();
        })
        .filter(Boolean);
    if (!list.length) return '';
    return `<ul>${list.map(item => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;
}

function labelled(label, text) {
    const value = String(text || '').trim();
    if (!value) return '';
    return `<p><strong>${escapeHtml(label)}</strong> ${escapeHtml(value)}</p>`;
}

function emptyHtml(data) {
    const empty = data?.emptyState || {};
    return (
        h2(empty.title || data?.title || 'Not generated') +
        p(empty.message || 'Generate an analysis version from Overview to populate this module.')
    );
}

function highlightsHtml(data) {
    if (!data?.isPopulated) return emptyHtml(data);
    return (data.cards || [])
        .map(
            card =>
                h2(card.displayLabel) +
                labelled('RAG', card.ragLabel) +
                h3(card.headline) +
                p(card.conclusion) +
                (card.whyItMatters ? `<p><strong>Why it matters</strong></p>${p(card.whyItMatters)}` : ''),
        )
        .join('');
}

function managementSummaryHtml(data) {
    if (data?.isEmpty) return emptyHtml(data);
    const overall = data.overall || {};
    const detail = data.overallDetail || {};
    return (
        labelled('Overall RAG', overall.ragLabel) +
        h3(overall.headline) +
        p(overall.summary) +
        p(overall.confidence) +
        p(data.companyOverview) +
        h2('Priority takeaways') +
        (data.takeaways || [])
            .map(item => h3(item.title) + p(item.body) + labelled('Materiality', item.materiality))
            .join('') +
        h2('Scope assessment') +
        (data.scopes || [])
            .map(scope => h3(scope.label) + labelled('RAG', scope.rag) + p(scope.text))
            .join('') +
        (detail.supportingStrengths?.length
            ? h2('Supporting strengths') + ul(detail.supportingStrengths)
            : '') +
        (detail.constrainingFactors?.length
            ? h2('Constraining factors') + ul(detail.constrainingFactors)
            : '')
    );
}

function keyFindingsHtml(data) {
    if (data?.isEmpty) return emptyHtml(data);
    return (data.areas || [])
        .map(area => {
            const items = area.items || {};
            return (
                h2(area.label) +
                labelled('RAG', area.rag) +
                p(area.summary) +
                p(area.rationale) +
                h3('Positives') +
                ul((items.positives || []).map(item => item.title || item.body)) +
                h3('Concerns') +
                ul((items.concerns || []).map(item => item.title || item.body)) +
                h3('Unresolved') +
                ul((items.unresolved || []).map(item => item.title || item.body))
            );
        })
        .join('');
}

function swotHtml(data) {
    if (data?.isEmpty) return emptyHtml(data);
    return (data.quadrants || [])
        .map(
            quad =>
                h2(`${quad.label} (${quad.itemCount ?? quad.items?.length ?? 0})`) +
                p(quad.definition) +
                (quad.items || [])
                    .map(item => h3(item.title) + p(item.body) + labelled('Materiality', item.materiality))
                    .join(''),
        )
        .join('');
}

function investmentOpinionHtml(data) {
    if (data?.isEmpty) return emptyHtml(data);
    const posture = data.posture || {};
    return (
        p(data.disclaimer) +
        labelled('Deal posture', posture.label) +
        h3(posture.headline) +
        p(posture.summary) +
        p(posture.confidence) +
        h2('Supporting factors') +
        (data.supportingFactors || []).map(item => h3(item.title) + p(item.body)).join('') +
        h2('Material concerns') +
        (data.materialConcerns || []).map(item => h3(item.title) + p(item.body)).join('') +
        h2('Conditions to resolve') +
        (data.conditions || []).map(item => h3(item.title) + p(item.body)).join('')
    );
}

function risksSignalsHtml(data) {
    if (data?.isEmpty) return emptyHtml(data);
    return (
        h2('Risk register') +
        (data.risks || [])
            .map(
                risk =>
                    h3(risk.title) +
                    labelled('Severity', risk.severity) +
                    p(risk.body) +
                    labelled('Status', risk.status) +
                    p(risk.detail?.whyRisk),
            )
            .join('') +
        ((data.signals || []).length ? h2('Signals') + ul(data.signals.map(s => s.label)) : '')
    );
}

function evidenceFollowUpsHtml(data) {
    if (data?.isEmpty) return emptyHtml(data);
    const position = data.overallPosition || {};
    return (
        h2('Overall evidence position') +
        labelled('Coverage', position.coverage) +
        p(position.summary) +
        h2('Coverage by scope') +
        (data.coverageMatrix || [])
            .map(
                row =>
                    h3(row.label) +
                    labelled('Coverage', row.coverage) +
                    labelled('Open matters', row.openMatters) +
                    p(row.primaryLimitation),
            )
            .join('') +
        h2('Follow-ups') +
        (data.followUps || data.matters || [])
            .map(item => h3(item.title) + p(item.body) + labelled('Priority', item.priority))
            .join('')
    );
}

function remediationHtml(data) {
    if (data?.isEmpty) return emptyHtml(data);
    const summary = data.summary || {};
    return (
        labelled('Y1 CAPEX', summary.year1CapexLabel) +
        labelled('Y1 OPEX', summary.year1OpexLabel) +
        labelled('Y2 CAPEX', summary.year2CapexLabel) +
        labelled('Y2 OPEX', summary.year2OpexLabel) +
        p(summary.disclaimer) +
        h2('Remediations') +
        (data.treatments || [])
            .map(
                item =>
                    h3(item.title) +
                    p(item.summary) +
                    labelled('Priority', item.priorityLabel) +
                    labelled('Timing', item.timeHorizonLabel) +
                    labelled('Y1 CAPEX', item.estimate?.year1CapexLabel) +
                    labelled('Y1 OPEX', item.estimate?.year1OpexLabel) +
                    labelled('Basis', item.estimate?.sourceLabel),
            )
            .join('')
    );
}

const BUILDERS = {
    highlights: highlightsHtml,
    management_summary: managementSummaryHtml,
    key_findings: keyFindingsHtml,
    swot_analysis: swotHtml,
    investment_opinion: investmentOpinionHtml,
    remediation_investment: remediationHtml,
    risks_signals: risksSignalsHtml,
    evidence_follow_ups: evidenceFollowUpsHtml,
};

export function resolveInsightsExportModuleKey(data) {
    if (data?.moduleKey) return data.moduleKey;
    if (Array.isArray(data?.cards)) return 'highlights';
    return null;
}

export function canExportInsightsModule(data) {
    if (!data || typeof data !== 'object') return false;
    const key = resolveInsightsExportModuleKey(data);
    if (!key || !BUILDERS[key]) return false;
    if (key === 'highlights') return Boolean(data.isPopulated);
    return data.isEmpty !== true;
}

export function buildInsightsExportHtml(data) {
    if (!data || typeof data !== 'object') return '';
    const header = p(data.subtitle) + p(data.metaLine);
    const key = resolveInsightsExportModuleKey(data);
    const builder = BUILDERS[key];
    const body = builder ? builder(data) : emptyHtml(data);
    return `${header}${body}`;
}

export async function exportIntelligenceInsightsPdf(data, { project, projectName } = {}) {
    if (!canExportInsightsModule(data)) {
        throw new Error('Nothing to export');
    }
    const title = data?.title || 'Intelligence Insights';
    const bodyHtml = buildInsightsExportHtml(data);
    if (!bodyHtml.trim()) {
        throw new Error('Nothing to export');
    }
    await exportNoteAsPdf({
        title,
        bodyHtml,
        includeTitle: true,
        filename: buildExportPdfFilename({
            project,
            projectName,
            label: title,
        }),
    });
}
